import { CREATE_NOTICE } from "../../../routes";
import { EVENT_REMINDER_OPTIONS } from "../../../constants/eventReminderOptions";
import { toCalendar } from "../../calendar/calendarHandover";
import type { EventRecurrenceFrequency } from "../../events/eventRecurrence";
import { INTERNAL_TEAM_ONLY_LABEL } from "../../notices/noticeAudience";
import { eventPlaceProblem, eventTimeProblem } from "../../notices/noticeRules";
import type { WizardHandover, WizardInitialFormData } from "../../notices/wizardSession";
import { capitalize } from "../catalogue/wording";
import { dayLabel, WEEKDAYS } from "../dateRange";
import { isFiller } from "../fillerWords";
import { DAY_PHRASE, dayGivenIn, findDay, MONTH, readTaskDetails, resolveDay, spelledOutDays, STAFF_ONLY, tidy, type DayKey } from "../taskDetails";
import { timeAlone, timeIn, timeLabel, twoDigits } from "../timeOfDay";
import { EVERYONE_WORDS, findRecipients, YOURS, type Recipient } from "./recipients";
import {
  CALLED_WHO,
  CHOOSE_IT_THERE,
  dayAlone,
  dayOfMonthCorrection,
  dayHasPassed,
  messageDetail,
  namesRecipient,
  preview,
  readDay,
  readRecipient,
  recipientInstruction,
  spotDay,
  spotRecipient,
  titleDetail,
  typed,
  whoQuestion as sharedWhoQuestion,
  withoutEndPunctuation,
} from "./sharedDetails";
import type { Question, TaskContext, TaskSchema } from "./taskConversation";

/** Who an Event is for: Everyone, one Circle, or the Internal team only. Never one User. */
type EventFor = Recipient | { kind: "team"; name: string };
const INTERNAL_TEAM: EventFor = { kind: "team", name: INTERNAL_TEAM_ONLY_LABEL };

type EventDetails = {
  title?: string;
  message?: string;
  day?: DayKey;
  /** "HH:MM", as the wizard takes it */
  time?: string;
  endTime?: string;
  place?: string;
  to?: EventFor;
  repeat?: EventRecurrenceFrequency;
  repeatUntil?: DayKey;
  rsvp?: boolean;
  reminders?: string[];
};

/** The text without the `length` characters found at `index`. */
const withoutPart = (text: string, index: number, length: number) =>
  tidy(text.slice(0, index) + " " + text.slice(index + length));

const WEEKDAY = WEEKDAYS.join("|");
const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];

/* The day */

/** "skip" or "not sure yet": leaves the day to be filled in the wizard, rather than looping on an unreadable day. */
const DAY_WRITTEN_THERE =
  /^(?:skip(?:\s+(?:it|that|this))?|not\s+sure(?:\s+yet)?|(?:i\s+)?don'?t\s+know(?:\s+yet)?|no\s+date(?:\s+yet)?|haven'?t\s+decided(?:\s+yet)?)[\s.!]*$/i;

/* Who it's for */

/** Who it's for, with Everyone (if they may) and their Circles to tap. */
const whoQuestion = (org: TaskContext) => sharedWhoQuestion("Who is it for?", org.everyoneAndCircles);

/** Saying the Internal team only, as a whole answer: "staff only", "the team", "Internal team only". */
const TEAM_WORDS =
  /^(?:(?:just|only)\s+(?:for\s+)?)?(?:the\s+)?(?:staff|teachers|internal\s+team|team|staff\s+members)(?:[- ]only)?(?:\s+then)?$/i;
/** "make it staff only", "it's for the Internal team only": only about who it's for. */
const TEAM_ALONE =
  /^(?:(?:make\s+it|it(?:'s|\s+is)|and|actually)\s+)*(?:staff[- ]only|internal team only|(?:just |only )?for (?:the )?(?:internal )?(?:team|staff)(?: only)?)(?:\s+then)?[\s.!]*$/i;

/** "Can parents RSVP?", asked instead of saying who it's for: answered, then asked again. */
const RSVP_QUESTION_LEAD = /^(?:can|could|will|do|does|is|are)\b/i;
const RSVP_ANSWER = "RSVP is switched on in Event settings, the step before Review.";
const asksAboutRsvp = (said: string) => {
  const text = typed(said);
  return /\brsvps?\b/i.test(text) && (/\?\s*$/.test(text) || RSVP_QUESTION_LEAD.test(text));
};

/* Times */

const TIME_QUESTION: Question = { text: "What time does it start?", choices: [] };
const TIME_EXAMPLE = "Try “2pm” or “14:30”.";
const UNREADABLE_TIME: Question = { text: `I couldn't read that time. ${TIME_EXAMPLE}`, choices: [] };

/** A time as an answer; an hour on its own is asked about, as it could be morning or afternoon. */
/** An Event needs a start time on Create, so "all day" can't be kept: said plainly, and the time asked for. */
const NO_ALL_DAY: Question = {
  text: "Events need a start time, so there isn't an all-day option. What time does it start? For something that runs all day, give the time it begins, like 8am.",
  choices: [],
};

function readTime(said: string): { value: string } | Question {
  if (ALL_DAY.test(said) && !timeIn(said)) return NO_ALL_DAY;
  const alone = timeAlone(said);
  if (alone && "hour" in alone) {
    const { hour, minutes } = alone;
    const suffix = minutes ? `:${twoDigits(minutes)}` : "";
    const label = (period: string) => `${hour}${suffix}${period}`;
    return { text: `Is that ${label("am")} or ${label("pm")}?`, choices: [label("am"), label("pm")], clarifies: true };
  }
  // "2pm sharp" as well as "2pm"
  const time = alone?.time ?? timeIn(said)?.time;
  return time && !eventTimeProblem(time) ? { value: time } : UNREADABLE_TIME;
}

/** "08:00 SHARP!!!", "2pm please": words after a time that don't change it. */
const AFTER_A_TIME = /(?:\s+(?:sharp|exactly|ish|please|pls|on the dot))+[\s.!]*$/i;

/** An answer that's only a time it can read: "3pm", "it starts at 3pm", "08:00 sharp". */
const timeOnly = (said: string) => {
  const alone = timeAlone(said.replace(/[!]+$/, "").replace(AFTER_A_TIME, ""));
  return alone && "time" in alone ? alone.time : undefined;
};

/** Words before a time that only say it's when it starts: "Sports day at 2pm", "from 2pm". */
const SAYS_STARTS = /\s*\b(?:(?:it\s+)?(?:starts?|starting|begins?)\s+)?(?:at|from)\s*$/i;

/** A shared am/pm belongs to both ends: “6 to 8pm” starts at 6pm. */
function withRangePeriods(said: string): string {
  return said.replace(/(?<![\d/])\b(\d{1,2}(?::\d{2})?)\s*(to|until|till|[-–])\s*(\d{1,2}(?::\d{2})?)\s*(am|pm)\b/gi, (all, start: string, join: string, end: string, period: string) => {
    const a = Number(start.split(":")[0]);
    const b = Number(end.split(":")[0]);
    if (a < 1 || a > 12 || b < 1 || b > 12) return all;
    const startPeriod = period.toLowerCase() === "pm" && a > b && a !== 12 ? "am" : period;
    return `${start}${startPeriod} ${join} ${end}${period}`;
  });
}

/** The one start time said in an answer to another question, and the answer without it. */
function spotTime(said: string) {
  const found = timeIn(said);
  if (!found) return undefined;
  const before = said.slice(0, found.index).replace(SAYS_STARTS, "");
  const rest = tidy(`${before} ${said.slice(found.index + found.length)}`);
  // "2pm or 3pm" is one or the other, so neither
  return timeIn(rest) ? undefined : { value: found.time, rest };
}

/** "it's all day", "the whole day", "full day event" */
const ALL_DAY = /\b(?:all|whole|full)[\s-]*day\b/i;

/**
 * "tomorrow at 10": an hour with no am or pm, in a request for an Event, read as a school's hours:
 * 7 to 11 in the morning, 12 to 6 in the afternoon. Anything else is asked.
 */
function spotSchoolHour(said: string) {
  const found = said.match(/\s*\bat\s+(\d{1,2})(?![\d:.]|\s*(?:am|pm|h\b|o'?clock))\b/i);
  if (found?.index === undefined) return undefined;
  const hour = Number(found[1]);
  const at = hour >= 7 && hour <= 11 ? hour : hour === 12 ? 12 : hour >= 1 && hour <= 6 ? hour + 12 : undefined;
  if (at === undefined) return undefined;
  return { value: `${twoDigits(at)}:00`, rest: tidy(`${said.slice(0, found.index)} ${said.slice(found.index + found[0].length)}`) };
}

/** An end time, said with the start and after it: "from 2pm to 4pm", "2pm until 4pm", "2pm-4pm". The start stays to be read. */
function spotEndTime(said: string) {
  said = withRangePeriods(said);
  const start = timeIn(said);
  if (!start) return undefined;
  const startEnds = start.index + start.length;
  const joined = said.slice(startEnds).match(/^\s*(?:to|until|till|-|–)\s*/i);
  const end = joined && timeIn(said.slice(startEnds + joined[0].length));
  if (!joined || !end || end.index !== 0 || end.time <= start.time) return undefined;
  return { value: end.time, rest: withoutPart(said, startEnds, joined[0].length + end.length) };
}

/* The place */

const PLACE_QUESTION: Question = { text: "Where is it?", choices: [] };

/** "in the hall", "at the school field", up to whatever's said next ("at the front gate because…"); "in aid of charity" and "at lunchtime" aren't places. */
const PLACE_PHRASE =
  /\s*\b(?:in|at)\s+(the\s+[^,.;!?]+?|(?:room|classroom|class|block|lab|hall)\s*[0-9][0-9a-z]*)(?=\s+\b(?:on|at|in|for|to|from|every|each|with|until|till|and|starting|starts|this|next|because|since|so|while|where|when|but)\b|[,.;!?]|$)/gi;
/** "in the morning", "at noon", "in November", "on the calendar": when, or where it's written down, never where it is. */
const NOT_A_PLACE = new RegExp(
  `^(?:the\\s+)?(?:morning|afternoon|evening|night|calendar|diary|term|holidays?|weekend|week|month|year|noon|midday|${WEEKDAY}|${MONTH})\\b`,
  "i"
);
/** What's said before a place: "it's in", "it's being held at". */
const BEFORE_A_PLACE = /^(?:it(?:'s|\s+is|\s+will\s+be)\s+)?(?:(?:being\s+)?held\s+)?(?:in|at)\s+/i;
/** "it's in the hall": only about the place. */
const PLACE_ALONE = /^(?:(?:and|oh|actually)[\s,]+)*it(?:'s|\s+is|\s+will\s+be)\s+(?:(?:being\s+)?held\s+)?(?:in|at)\s+(.+)$/i;

/** "the school field" as the wizard shows it: "School field". */
const placeName = (place: string) => capitalize(tidy(place).replace(/^the\s+/i, ""));

/** ", where else!", ", obviously": an aside after the place, not part of its name. */
const AFTER_A_PLACE = /\s*,\s*(?:where else|obviously|of course|as always|as usual|please|thanks|pls)\b.*$/i;

function readPlace(said: string): { value: string } | Question {
  const place = withoutEndPunctuation(said).replace(AFTER_A_PLACE, "").replace(BEFORE_A_PLACE, "");
  if (!place || isFiller(place)) return PLACE_QUESTION;
  const name = placeName(place);
  return eventPlaceProblem(name) ? PLACE_QUESTION : { value: name };
}

function spotPlace(said: string) {
  for (const found of Array.from(said.matchAll(PLACE_PHRASE))) {
    if (found.index === undefined || NOT_A_PLACE.test(found[1])) continue;
    return { value: placeName(found[1]), rest: withoutPart(said, found.index, found[0].length) };
  }
  return undefined;
}

/* Repeats */

const REPEATS: Array<[RegExp, EventRecurrenceFrequency]> = [
  [new RegExp(`\\bevery\\s+(?:two|2|other|second)\\s+(?:weeks?|${WEEKDAY})\\b|\\bfortnightly\\b|\\bbi-?weekly\\b`, "i"), "biweekly"],
  [/\bevery\s+day\b|\beach\s+day\b|\bdaily\b/i, "daily"],
  [new RegExp(`\\b(?:every|each)\\s+(?:week|${WEEKDAY})\\b|\\bweekly\\b`, "i"), "weekly"],
  [/\b(?:every|each)\s+month\b|\bmonthly\b/i, "monthly"],
];
/** "repeating", "and repeat" before how often. */
const SAYS_REPEATS = /\s*\b(?:and\s+)?(?:repeat(?:s|ing)?|recurring)\s*$/i;
const REPEAT_LABELS: Record<EventRecurrenceFrequency, string> = {
  daily: "Daily",
  weekly: "Weekly",
  biweekly: "Every 2 weeks",
  monthly: "Monthly",
};

function spotRepeat(said: string) {
  for (const [pattern, frequency] of REPEATS) {
    const found = said.match(pattern);
    if (found?.index === undefined) continue;
    const before = said.slice(0, found.index).replace(SAYS_REPEATS, "");
    return { value: frequency, rest: tidy(`${before} ${said.slice(found.index + found[0].length)}`) };
  }
  return undefined;
}

/** "every Friday" says the day too: the coming Friday. Left in the answer, as it also says how often. */
function spotEveryWeekday(said: string) {
  const found = said.match(new RegExp(`\\b(?:every|each)\\s+(${WEEKDAY})\\b`, "i"));
  return found ? { value: found[1].toLowerCase() as DayKey, rest: said } : undefined;
}

const UNTIL_QUESTION: Question = { text: "When does it stop repeating?", choices: [] };
const UNTIL_EXAMPLE = "Try “until 12 December”.";
/** "until December", "till the end of November", "until 12 December". */
const UNTIL_PHRASE =
  /\s*\b(?:until|till|til|up\s+to|ending(?:\s+on)?)\s+((?:the\s+)?[^,.;!?]+?)(?=\s+\b(?:at|in|for|from|with|and)\b|[,.;!?]|$)/i;
const SAYS_UNTIL = /^(?:(?:it\s+)?(?:stops|ends|finishes)(?:\s+on)?|until|till|til|up\s+to|on)\s+/i;

/** The day a repeat stops: a date ("12 December"), a day ("Friday"), or a month: its first day, or its last for "the end of November". */
function untilDay(said: string): DayKey | null {
  const phrase = withoutEndPunctuation(said).replace(SAYS_UNTIL, "").replace(/^the\s+/i, "");
  const day = findDay(phrase);
  if (day) return day;
  const month = phrase.match(new RegExp(`^(end\\s+of\\s+)?(${MONTH})$`, "i"));
  if (!month) return null;
  const index = MONTHS.indexOf(month[2].slice(0, 3).toLowerCase());
  const lastDay = new Date(2001, index + 1, 0).getDate();
  return `date:${twoDigits(index + 1)}-${twoDigits(month[1] ? lastDay : 1)}`;
}

function spotRepeatUntil(said: string, org: TaskContext) {
  const found = said.match(UNTIL_PHRASE);
  const day = found && untilDay(found[1]);
  if (!found || found.index === undefined || !day || dayHasPassed(day, org.today)) return undefined;
  return { value: day, rest: withoutPart(said, found.index, found[0].length) };
}

/* RSVP and reminders */

const NO_RSVP = /\b(?:no|without)\s+(?:an?\s+)?rsvps?\b/i;
const RSVP_PHRASE = /\s*\b(?:and\s+)?(?:with\s+(?:an?\s+)?|add\s+(?:an?\s+)?|turn\s+on\s+)?rsvps?\b(?:\s+(?:on|enabled))?/i;
const RSVP_ALONE = /^(?:(?:and|also|please)[\s,]+)*(?:(?:with|add|turn on)\s+(?:an?\s+)?)?rsvps?(?:\s+on)?[\s.!]*$/i;

const spotRsvp = (said: string) =>
  RSVP_PHRASE.test(said) && !NO_RSVP.test(said) ? { value: true, rest: tidy(said.replace(RSVP_PHRASE, " ")) } : undefined;

/** When a reminder goes out, as said, and the wizard's reminder for it. */
const REMINDER_WORDS: Array<[RegExp, string]> = [
  [/\b(?:2|two)\s+days\s+before\b/i, "2_days_before_at_19:00"],
  [/\b(?:(?:a|1|one)\s+day|the\s+(?:day|night|evening))\s+before\b/i, "1_day_before_at_19:00"],
  [/\b(?:an?|1|one)\s+hour\s+before\b/i, "1_hour_before"],
  [/\b(?:5|five)\s+min(?:ute)?s?\s+before\b/i, "5_minutes_before"],
  [/\bwhen\s+it\s+starts\b|\bat\s+the\s+start\b/i, "at_event_start"],
];
const REMINDER_PHRASE =
  /\s*\b(?:and\s+)?(?:with\s+(?:a\s+)?reminders?|remind(?:\s+(?:them|parents|everyone|people|families|us))?)\s+([^.;!?]+)/i;
const REMINDER_QUESTION: Question = {
  text: "I couldn't tell when to send the reminder. Try “an hour before” or “the day before”.",
  choices: [],
};

/** The reminders said, in the order the wizard lists them. */
const remindersIn = (said: string) => {
  const modes = REMINDER_WORDS.filter(([words]) => words.test(said)).map(([, mode]) => mode);
  return EVENT_REMINDER_OPTIONS.map((o) => o.value).filter((mode) => modes.includes(mode));
};

function spotReminders(said: string) {
  const found = said.match(REMINDER_PHRASE);
  const modes = found ? remindersIn(found[1]) : [];
  return found?.index !== undefined && modes.length ? { value: modes, rest: withoutPart(said, found.index, found[0].length) } : undefined;
}

/* The request */

/** What's being put on the calendar: "put sports day on the calendar". */
const ON_THE_CALENDAR = /\b(?:put|add|pop|stick|get|enter|schedule)\s+(.+?)\s+(?:on(?:to)?|in(?:to)?|to)\s+(?:the|our|my)\s+calendar\b/i;
/** The text without its day phrases, "nxt thurs" included. */
const withoutDays = (text: string) => tidy(spelledOutDays(text).replace(DAY_PHRASE, " "));

/** What an Event is: "an event for the swimming gala", "an event called Sports day". */
const EVENT_FOR =
  /\bevent\s+(?:for|called|named|about)\s+(.+?)(?=\s+\b(?:on|at|in|for|to|from|every|with|until|and)\b|[,.?!]|$)/i;
/** Saying a thing, not what it is: "add an event to the calendar", "put something on the calendar". */
const NOT_A_TITLE = /^(?:(?:an?|the|new|some)\s+)?(?:events?|dates?|something|it|this|that)$/i;
/** Words that name an occasion rather than a Circle: "for the Grade 7 farewell" is the Event's title. */
const OCCASION_WORDS =
  /\b(farewell|gala|meeting|day|concert|party|sale|trip|evening|night|lesson|match|ceremony|service|celebration|fair|market|show|festival|outing|assembly|camp|practice|exam|function|dinner|lunch|breakfast|braai|picnic|ball|disco|tournament|race|play|prize-?giving|graduation|open day|clinic|drive|dance|parade|fundraiser|bee|final|launch|workshop|meet|session)s?\b/i;
/** "mk event parents evening…": what it is, said straight after the word "event". */
const AFTER_EVENT =
  /\bevents?\s+(?!(?:for|called|named|about|on|at|in|to|from|with|and|every|this|next|pls|please|first|now|too|then|again|instead|later|asap)\b)([^,.;!?:]+)/i;
/** "…6pm hall": a place said bare at the end, when it's one every school has. */
const BARE_PLACE =
  /\s+(?:in\s+|at\s+)?(?:the\s+)?((?:school\s+|main\s+|sports\s+)?(?:hall|library|field|gym|office|classroom(?:\s+[0-9]+[a-z]?)?|staff\s?room|front\s+gate|chapel|auditorium|canteen|tuck\s?shop|science lab|music room|community cent(?:re|er)|courtyard|playground|swimming pool))[\s.!]*$/i;

/** ", the park", ", at Johannesburg Zoo": a short last part of a list, said after when it is. */
const LAST_PART_PLACE = /,\s*(?:(?:at|in)\s+)?((?:the\s+)?[a-z][\w'’-]*(?:\s+[\w'’-]+){0,3})[\s.!]*$/i;
/** Never a place: who it's for, or how it's said. */
const NOT_A_LAST_PLACE = /\b(?:grade|gr|class|parents|families|everyone|staff|team|please|pls|thanks|asap|every|weekly|daily)\b/i;

/** "event tomorrow at 10 in the hall: Grade 4 assembly": the request, when and where, then the name. */
const WHEN_THEN_NAME = /^(.*?\bevents?)(?:\s*:\s*|\s+)((?:[^:]|\d:\d)+?)(?<!\d):(?!\d)\s*(.+)$/i;
/** One place phrase, to tell "…in the hall:" from "…for Grade 4:" */
const PLACE_PHRASE_ONE = /\b(?:in|at)\s+(?:the\s+\w+|(?:room|classroom|hall)\s*\d)/i;
/** "… for TEST Grade 4 2027", "… to Everyone": who it's for, said at the very end. */
const FOR_AT_THE_END = /,?\s+(?:for|to)\s+((?:the\s+)?(?:[\w'’-]+\s+){0,4}[\w'’-]+)[\s.!]*$/i;

const withoutThe = (text: string) => text.replace(/^(?:the|our)\s+/i, "");

/**
 * The details a request for an Event gives. When, where, how often and the reminders
 * are read out first, so what's left says what it is and who it's for: "Create an
 * event for the Grade 7 farewell on 28 November at 6pm" is the Grade 7 farewell.
 */
function readRequest(request: string, org: TaskContext) {
  const audienceBeforeName = request.match(/^(.*?\bevent)\s+for\s+(.+?)\s+called\s+(.+)$/i);
  const namedAudience = audienceBeforeName && (findRecipients(org.everyoneAndCircles, audienceBeforeName[2]).length || EVERYONE_WORDS.test(audienceBeforeName[2].toLowerCase())) ? audienceBeforeName[2] : undefined;
  if (namedAudience && audienceBeforeName) request = `${audienceBeforeName[1]} called ${audienceBeforeName[3]}`;
  request = withRangePeriods(request).replace(/\bjust the staff\b/gi,"internal team only");
  // Remove an explicit staff audience before looking for a bare venue.
  const staffAudience = STAFF_ONLY.test(request);
  if (staffAudience) request = request.replace(STAFF_ONLY, " ").replace(/[, ]+$/, "").trim();
  // A last bare audience is still an audience in a compact Event form.
  const words=Array.from(request.matchAll(/\S+/g));
  for(let i=Math.max(0,words.length-7); i<words.length; i++) {
    const start=words[i].index ?? 0;
    const candidate=request.slice(start).replace(/^[, ]+|[, .!?]+$/g,"");
    if(findRecipients(org.everyoneAndCircles,candidate).length===1) {
      if(!/\b(?:for|to)\s*$/i.test(request.slice(0,start)) && !/\s[-–—]\s/.test(request) && (findDay(request.slice(0,start)) || timeIn(request.slice(0,start))))request=`${request.slice(0,start).replace(/[, ]+$/,"")} for ${candidate}`;
      break;
    }
  }
  const parts = request.split(/\s+[-–—]\s+/);
  if (parts.length >= 4 && (findDay(parts[1]) || findDay(`on ${parts[1]}`)) && timeIn(parts[2])) {
    request = `${parts[0]} on ${parts[1]} at ${parts[2]} in the ${parts[3]}${parts[4] ? ` for ${parts[4]}` : ""}`;
  }
  request = request.replace(/\b(?:place|venue|location)\s*(?::|is\b)\s*(?:the\s+)?/gi, "in the ")
    .replace(/\b(?:day|date)\s*:\s*/gi, "on ")
    .replace(/\btime\s*:\s*/gi, "at ")
    .replace(/\baudience\s*(?::|is\b)?\s*/gi, "for ")
    .replace(/[.;]\s*(?=(?:in the|on |at |for ))/gi, " ");

  // "event tomorrow at 10 in the hall: Grade 4 assembly": the name after the colon, when what's before it says when or where
  const nameLast = request.match(WHEN_THEN_NAME);
  let rest =
    nameLast && (findDay(nameLast[2]) || timeIn(nameLast[2]) || PLACE_PHRASE_ONE.test(nameLast[2]))
      ? `${nameLast[1]}: ${nameLast[3]} ${nameLast[2]}`
      : request;
  const read: Record<string, { value: unknown }> = {};
  // "…in Room 12 for TEST Grade 4 2027": who it's for, said last, after the name and the details
  const lastFor = rest.match(FOR_AT_THE_END);
  const lastForFound = lastFor?.index !== undefined ? findRecipients(org.everyoneAndCircles, lastFor[1]) : [];
  if (lastFor?.index !== undefined && lastForFound.length === 1) {
    read.to = { value: lastForFound[0] };
    rest = rest.slice(0, lastFor.index);
  }
  const take = (name: string, spot: (said: string, org: TaskContext) => { value: unknown; rest: string } | undefined) => {
    const found = spot(rest, org);
    if (!found) return;
    read[name] = { value: found.value };
    rest = found.rest;
  };
  take("repeatUntil", spotRepeatUntil);
  take("endTime", spotEndTime);
  take("time", spotTime);
  if (!read.time) take("time", spotSchoolHour);
  take("reminders", spotReminders);
  take("rsvp", spotRsvp);
  // "every Friday" is the coming Friday, so the day is read before how often
  const day = findDay(rest, org.today) ?? undefined;
  take("repeat", spotRepeat);
  take("place", spotPlace);
  take("place", (said) => {
    const found = said.match(BARE_PLACE);
    return found?.index !== undefined && !read.place ? { value: placeName(found[1]), rest: said.slice(0, found.index) } : undefined;
  });
  // "Picnic for Grade 4, 4 December 12pm, the park": after when it is, the last part is where
  take("place", (said) => {
    if (read.place || (!read.time && !day)) return undefined;
    const found = said.match(LAST_PART_PLACE);
    const place = found?.[1].trim();
    if (found?.index === undefined || !place || findDay(place) || timeIn(place) || NOT_A_LAST_PLACE.test(place)) return undefined;
    return { value: placeName(place), rest: said.slice(0, found.index) };
  });

  const { said } = readTaskDetails(rest);
  // "just for the team" says who it's for, never what it's called
  if (said.staffOnly) rest = tidy(rest.replace(STAFF_ONLY, " "));
  const audience = said.audience?.replace(/\s+\b(?:in|from|every|with|until)\b.*$/i, "");
  const occasion = !said.topic && audience && OCCASION_WORDS.test(audience) ? audience : undefined;
  const onCalendar = rest.match(ON_THE_CALENDAR)?.[1];
  const forPhrase = rest.match(EVENT_FOR)?.[1];
  const eventFor = forPhrase ? withoutDays(forPhrase) : undefined;
  const afterEvent = withoutDays(rest).match(AFTER_EVENT)?.[1]?.trim();
  // "an event for the measles vaccination clinic": not a Circle, so what it's for is the Event's name
  const forACircle = Boolean(audience && (findRecipients(org.everyoneAndCircles, audience).length || EVERYONE_WORDS.test(audience.toLowerCase())));
  const named = [said.topic, occasion, onCalendar, eventFor !== audience || !forACircle ? eventFor : undefined, afterEvent].find(
    (title) => title && !isFiller(title) && !NOT_A_TITLE.test(title) && !YOURS.test(title.trim()) && !findRecipients(org.everyoneAndCircles, title).length
  );
  // "about sports day on Friday" is about sports day
  const title = named && withoutThe(dayGivenIn(named, org.today)?.rest ?? named);
  return {
    title,
    day,
    to: namedAudience ?? ((staffAudience || (said.staffOnly && !forACircle)) ? INTERNAL_TEAM_ONLY_LABEL : occasion || (audience && audience === eventFor && !forACircle) ? undefined : audience),
    ...read,
  };
}

/** "Please collect your child…", "Bring…": an instruction to whoever reads it, so its times are theirs, not the Event's. */
const INSTRUCTION_FIRST = /^(?:please|collect|bring|remember|don'?t|do not|make sure|kindly|note)\b/i;

/**
 * A message that leads with when it is: "Friday 30 October, 08:00-14:00 at the main field", "staff meeting
 * next wednesday 14:30 to 15:30 in the library". A sentence says the day in its first few words, and isn't an
 * instruction: "Please collect your child at 3pm on Friday" isn't when the Event starts.
 */
function leadsWithTheDay(said: string): boolean {
  return said
    .split(/[.!?]+\s+/)
    .some((sentence) => {
      const words = sentence.trim();
      if (!words || INSTRUCTION_FIRST.test(words) || !dayGivenIn(words)) return false;
      const opening = words.split(/\s+/).slice(0, 7).join(" ");
      return Boolean(findDay(opening));
    });
}

/* The schema */

const recipientsOf = (org: TaskContext) => org.everyoneAndCircles;

const isTeam = (said: string) => TEAM_WORDS.test(withoutEndPunctuation(said));

/**
 * The end time and reminders as the wizard keeps them: an end time only while it's after
 * the start (the start may have changed since), and no reminders for the Internal team only.
 */
const settingsKept = ({ time, endTime, to, reminders }: EventDetails) => ({
  endTime: time && endTime && endTime > time ? endTime : undefined,
  reminders: to?.kind === "team" ? undefined : reminders,
});

/** Where the wizard puts who it's for. */
const audienceOf = (to: EventFor | undefined): WizardInitialFormData => {
  switch (to?.kind) {
    case "team":
      return { staffOnly: true };
    case "everyone":
      return { audience: "circle" };
    case "circle":
      return { audience: "subcircle", targetSubcircle: to.id };
    default:
      return {};
  }
};

/**
 * An Event: a title, message, day, start time, place and who it's for, asked in that
 * order (who it's for only when it isn't for the Internal team only). An end time,
 * repeat, RSVP and reminders are only taken when said; a repeat then needs the day it stops.
 */
export const eventTask: TaskSchema = {
  task: "event",
  details: [
    // "Sports day on Friday at 2pm in the hall for Grade 3" is Sports day, and when, where and who it's for
    titleDetail("Event", ["repeatUntil", "endTime", "time", "reminders", "rsvp", "to", "day", "repeat", "place"]),
    // A message that names a day gives the day, time and place too ("Friday 30 October, 08:00-14:00 at the
    // main field"); one that names none gives nothing: "collect your child at 3pm" isn't when the Event starts
    messageDetail(["repeatUntil", "endTime", "time", "day", "repeat", "place"], leadsWithTheDay),
    {
      name: "day",
      required: true,
      called: /^(?:date|day)$/i,
      step: "eventInfo",
      question: () => ({ text: "What day is it?", choices: [] }),
      read: readDay,
      spot: (said, org) => spotDay(said, org) ?? spotEveryWeekday(said),
      instruction: (said, org) => dayAlone(said, org.today) ?? dayOfMonthCorrection(said, org.today),
      isValue: (said, org) => Boolean(dayAlone(said, org.today) ?? dayOfMonthCorrection(said, org.today)),
      // "Friday at 2pm, every week": the repeat too, never dropped; "Friday 30 October, 08:00-14:00 at the main field" the place
      alsoReads: ["repeatUntil", "repeat", "endTime", "time", "place"],
      writtenThere: DAY_WRITTEN_THERE,
    },
    {
      name: "time",
      required: true,
      called: /^(?:(?:start(?:ing)?\s+)?time|start)$/i,
      step: "eventInfo",
      question: () => TIME_QUESTION,
      read: readTime,
      spot: spotTime,
      instruction: (said) => (timeOnly(said) ? said : undefined),
      isValue: (said) => Boolean(timeOnly(said)),
      alsoReads: ["repeatUntil", "repeat", "endTime", "day"],
    },
    {
      name: "place",
      required: true,
      called: /^(?:place|location|venue|where(?: it is)?)$/i,
      step: "eventInfo",
      question: () => PLACE_QUESTION,
      read: readPlace,
      spot: spotPlace,
      instruction: (said) => {
        const place = typed(said).match(PLACE_ALONE)?.[1];
        return place && !NOT_A_PLACE.test(place) ? place : undefined;
      },
      // "the hall at 2pm on Friday for Grade 3"
      alsoReads: ["endTime", "time", "to", "day"],
    },
    {
      name: "to",
      required: true,
      called: CALLED_WHO,
      step: "review",
      question: (org) => whoQuestion(org)(),
      read: (said, org) => {
        if (isTeam(said)) return { value: INTERNAL_TEAM };
        const recipient = readRecipient(said, recipientsOf(org), org, whoQuestion(org));
        // Only a name it couldn't find at all is worth checking against a known
        // question; "Which one do you mean?" already has its own answer.
        if (!("value" in recipient) && !recipient.clarifies && asksAboutRsvp(said)) {
          const { text, choices } = whoQuestion(org)();
          return { text: `${RSVP_ANSWER} ${text}`, choices, clarifies: true };
        }
        return recipient;
      },
      spot: (said, org) => {
        const staff = said.match(STAFF_ONLY);
        if (staff?.index !== undefined) return { value: INTERNAL_TEAM, rest: withoutPart(said, staff.index, staff[0].length) };
        return spotRecipient(said, recipientsOf(org));
      },
      instruction: (said, org) =>
        TEAM_ALONE.test(typed(said)) ? "staff only" : recipientInstruction(said, recipientsOf(org), org),
      isValue: (said, org) => isTeam(said) || namesRecipient(said, recipientsOf(org), org),
      writtenThere: CHOOSE_IT_THERE,
      requiresExplicitSkip: true,
    },
    {
      name: "repeatUntil",
      // The wizard needs the day a repeating Event stops
      required: (details) => details.repeat !== undefined,
      called: /^(?:(?:the\s+)?(?:end|last)(?:\s+(?:date|day))?|until)$/i,
      step: "eventSettings",
      question: () => UNTIL_QUESTION,
      read: (said, org) => {
        const day = untilDay(said);
        if (!day) return { text: `I couldn't read that day. ${UNTIL_EXAMPLE}`, choices: [] };
        return dayHasPassed(day, org.today) ? { text: `That day has already passed. ${UNTIL_EXAMPLE}`, choices: [] } : { value: day };
      },
      spot: spotRepeatUntil,
      isValue: (said) => Boolean(untilDay(said)),
    },
    {
      name: "endTime",
      called: /^(?:end(?:ing)?|finish(?:ing)?)(?:\s+time)?$/i,
      step: "eventInfo",
      read: (said) => {
        const time = timeOnly(said);
        return time ? { value: time } : UNREADABLE_TIME;
      },
      spot: spotEndTime,
    },
    {
      name: "repeat",
      called: /^(?:repeats?|how often)$/i,
      step: "eventSettings",
      read: (said) => {
        const found = spotRepeat(said);
        return found ? { value: found.value } : { text: "I couldn't tell how often it repeats. Try “every week”.", choices: [] };
      },
      spot: spotRepeat,
    },
    {
      name: "rsvp",
      called: /^rsvps?$/i,
      step: "eventInfo",
      read: () => ({ value: true }),
      spot: spotRsvp,
      instruction: (said) => (RSVP_ALONE.test(typed(said)) ? "rsvp" : undefined),
      isValue: (said) => /^rsvp$/i.test(withoutEndPunctuation(said)),
    },
    {
      name: "reminders",
      called: /^reminders?$/i,
      step: "eventSettings",
      read: (said) => {
        const modes = remindersIn(said);
        return modes.length ? { value: modes } : REMINDER_QUESTION;
      },
      spot: spotReminders,
      instruction: (said) => /^remind\s+(?:them|parents|everyone)\b/i.test(said.trim()) ? said : undefined,
    },
  ],
  cannotStart: () => undefined,
  saidIn: (said, request, org) => (request ? readRequest(request, org) : { to: said.audience, day: said.day }),
  summary: (details, org) => {
    const { title, message, day, time, place, to, repeat, repeatUntil, rsvp } = details as EventDetails;
    const { endTime, reminders } = settingsKept(details as EventDetails);
    const until = repeatUntil ? ` until ${dayLabel(resolveDay(repeatUntil, org.today))}` : "";
    return [
      ...(title ? [{ label: "Title", value: title }] : []),
      { label: "For", value: to?.name ?? "choose on the next screen" },
      ...(day ? [{ label: "Date", value: `${dayLabel(resolveDay(day, org.today))}${/^date:\d{4}-/.test(day) ? `, ${resolveDay(day, org.today).getFullYear()}` : ""}` }] : []),
      ...(time ? [{ label: "Time", value: endTime ? `${timeLabel(time)} – ${timeLabel(endTime)}` : timeLabel(time) }] : []),
      ...(place ? [{ label: "Place", value: place }] : []),
      ...(repeat ? [{ label: "Repeats", value: `${REPEAT_LABELS[repeat]}${until}` }] : []),
      ...(rsvp ? [{ label: "RSVP", value: "On" }] : []),
      ...(reminders
        ? [{ label: "Reminders", value: EVENT_REMINDER_OPTIONS.filter((o) => reminders.includes(o.value)).map((o) => o.label).join(", ") }]
        : []),
      ...(message ? [{ label: "Message", value: preview(message) }] : []),
    ];
  },
  handover: (details, org, { startAt, onCalendar }) => {
    // Every handover opens on the same step, so RSVP, Reminder and Repeat are always seen; a
    // detail left for the wizard to fill in opens on its own step instead; from the Calendar,
    // the day is already chosen, so Review is enough.
    const openAt = startAt ?? (onCalendar ? "review" : "eventSettings");
    const { title = "", message = "", day, time, place, to, repeat, repeatUntil, rsvp } = details as EventDetails;
    const { endTime, reminders } = settingsKept(details as EventDetails);
    const date = day && resolveDay(day, org.today);
    const initialFormData: WizardInitialFormData = {
      title,
      // As typed: the wizard shows plain text as it is, or turns it into paragraphs when formatting is on
      body: message,
      ...(date ? { eventStartDate: date } : {}),
      // Only a time they gave: the wizard never starts it at 09:00 without saying
      ...(time ? { eventStartTime: time } : {}),
      ...(date && endTime ? { eventEndDate: date, eventEndTime: endTime } : {}),
      ...(place ? { eventLocation: place } : {}),
      ...(repeat ? { eventRecurrenceFrequency: repeat } : {}),
      ...(repeatUntil ? { eventRecurrenceUntil: resolveDay(repeatUntil, org.today) } : {}),
      ...(rsvp ? { rsvpEnabled: true } : {}),
      ...(reminders ? { eventReminderModes: reminders } : {}),
      ...audienceOf(to),
    };
    if (onCalendar) return toCalendar({ addEvent: { date: null, startAt: openAt, initialFormData } });
    const state: WizardHandover = { prefillNotice: { noticeType: "custom_event", startAt: openAt, initialFormData } };
    return { pathname: CREATE_NOTICE, state };
  },
  readyText: "Here's your Event. Nothing is published until you press Publish.",
  handedOverText: "Here it is. Nothing is published until you press Publish.",
  fillInThereText: "No problem, I'll open the Event so you can fill that in there.",
};

